<script setup>
import { computed, nextTick, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import { useCrmStore } from '../store';
import {
  dealTasksAPI,
  dealItemsAPI,
  itemTemplatesAPI,
  dealAttachmentsAPI,
} from '../api';
import { formatAmountInput, formatMoney } from '../helpers';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConfirmDialog from 'dashboard/wasapi/common/ConfirmDialog.vue';
import DialogSteps from 'dashboard/wasapi/common/DialogSteps.vue';

const props = defineProps({
  stages: { type: Array, default: () => [] },
  lockContact: { type: Boolean, default: false },
});

const emit = defineEmits(['saved', 'deleted']);

const { t, locale } = useI18n();
const store = useCrmStore();
const agents = useMapGetter('agents/getVerifiedAgents');

const dialogRef = ref(null);
const editingId = ref(null);
const tasks = ref([]);
const newTask = ref('');
const items = ref([]);
const itemTemplates = ref([]);
const pickedTemplateId = ref('');
const attachments = ref([]);
const uploadingFiles = ref([]);
const fileInput = ref(null);
const contactOptions = ref([]);
const contactLabel = ref('');

const blankForm = () => ({
  name: '',
  amount: '',
  stageId: '',
  contactId: '',
  conversationId: null,
  assigneeId: '',
  expectedCloseDate: '',
  description: '',
  closeReasonId: '',
  customAttributes: {},
});
const form = reactive(blankForm());

const isEditing = computed(() => !!editingId.value);
const itemsTotalCents = computed(() =>
  items.value.reduce(
    (sum, i) =>
      sum +
      Math.round(
        (Number(i.quantity) || 0) * (Number(i.unit_amount_cents) || 0)
      ),
    0
  )
);
const hasItems = computed(() => items.value.length > 0);
const money = cents => formatMoney(cents, 'BRL', locale.value);
const templateOptions = computed(() => [
  { value: '', label: t('WASAPI_CRM.ITEMS.FROM_CATALOG') },
  ...itemTemplates.value.map(t2 => ({
    value: t2.id,
    label: `${t2.name} · ${money(t2.unit_amount_cents)}`,
  })),
]);
const isSaving = computed(() => store.uiFlags.savingDeal);
// accepts "1500", "1500.50", "1.500,50" and "1,500.50"; null = unparseable
const parseAmount = raw => {
  const text = String(raw ?? '').trim();
  if (!text) return 0;
  let normalized = text.replace(/[^\d.,-]/g, '');
  const lastComma = normalized.lastIndexOf(',');
  const lastDot = normalized.lastIndexOf('.');
  if (lastComma > lastDot) {
    normalized = normalized.replace(/\./g, '').replace(',', '.');
  } else {
    normalized = normalized.replace(/,/g, '');
  }
  const value = Number(normalized);
  return Number.isFinite(value) ? Math.round(value * 100) : null;
};

const stageOptions = computed(() =>
  props.stages.map(s => ({ value: s.id, label: s.name }))
);
// When editing, allow moving to any stage of any pipeline (transfer keeps contact, conversation,
// fields and owner — it is the same deal row). Creating from a conversation also offers every
// pipeline, so group by funnel there too — a flat list gives no clue which funnel a stage is from.
const spansPipelines = computed(
  () =>
    new Set(
      props.stages
        .map(
          s => store.pipelines.find(p => p.stages.some(x => x.id === s.id))?.id
        )
        .filter(Boolean)
    ).size > 1
);
const stageGroups = computed(() =>
  isEditing.value || spansPipelines.value
    ? store.pipelines.map(p => ({
        label: p.name,
        options: p.stages.map(s => ({ value: s.id, label: s.name })),
      }))
    : []
);
// O ComboBox é uma lista única: quando a escolha cruza funis, o nome do funil vira prefixo
// do rótulo, então a busca encontra tanto pela etapa quanto pelo funil.
const stagePickerOptions = computed(() =>
  stageGroups.value.length
    ? stageGroups.value.flatMap(group =>
        group.options.map(option => ({
          value: option.value,
          label: `${group.label} · ${option.label}`,
        }))
      )
    : stageOptions.value
);
const targetPipeline = computed(() =>
  store.pipelines.find(p => p.stages.some(s => s.id === form.stageId))
);
const customFields = computed(() => targetPipeline.value?.customFields || []);
// Closing a deal from here must offer the same won/lost reason the board asks for on drag —
// otherwise a pipeline that requires one makes the dialog impossible to save.
const targetStage = computed(
  () => targetPipeline.value?.stages.find(s => s.id === form.stageId) || null
);
const closeReasons = computed(() => {
  const kind = targetStage.value?.category;
  if (kind !== 'won' && kind !== 'lost') return [];
  return (targetPipeline.value?.closeReasons || []).filter(
    r => r.kind === kind && r.isActive !== false
  );
});
const isCloseReasonRequired = computed(() => {
  if (!closeReasons.value.length) return false;
  return targetStage.value?.category === 'won'
    ? !!targetPipeline.value?.requireWonReason
    : !!targetPipeline.value?.requireLostReason;
});
const closeReasonOptions = computed(() => [
  ...(isCloseReasonRequired.value
    ? []
    : [{ value: '', label: t('WASAPI_CRM.CLOSE_REASON.NONE') }]),
  ...closeReasons.value.map(r => ({ value: r.id, label: r.name })),
]);
const isInvalid = computed(
  () =>
    !form.name.trim() ||
    !form.stageId ||
    parseAmount(form.amount) === null ||
    (isCloseReasonRequired.value && !form.closeReasonId)
);

const agentOptions = computed(() => [
  { value: '', label: t('WASAPI_CRM.DEAL.FIELDS.UNASSIGNED') },
  ...(agents.value || []).map(a => ({ value: a.id, label: a.name })),
]);

const loadTasks = async dealId => {
  try {
    tasks.value = (await dealTasksAPI(dealId).list()).data.payload || [];
  } catch (e) {
    tasks.value = [];
  }
};
const addTask = async () => {
  const title = newTask.value.trim();
  if (!title || !editingId.value) return;
  const { data } = await dealTasksAPI(editingId.value).create(title);
  tasks.value.push(data);
  newTask.value = '';
};
const toggleTask = async task => {
  const { data } = await dealTasksAPI(editingId.value).update(task.id, {
    done: !task.done,
  });
  Object.assign(task, data);
};
const removeTask = async task => {
  await dealTasksAPI(editingId.value).delete(task.id);
  tasks.value = tasks.value.filter(item => item.id !== task.id);
};

const loadAttachments = async dealId => {
  try {
    attachments.value =
      (await dealAttachmentsAPI(dealId).list()).data.payload || [];
  } catch (e) {
    attachments.value = [];
  }
};
const onUpload = async event => {
  const files = Array.from(event.target.files || []);
  if (!files.length || !editingId.value) return;
  const fd = new FormData();
  files.forEach(f => fd.append('files[]', f));
  // keep the picked files on screen while they travel, so the upload is visible
  uploadingFiles.value = files.map(f => f.name);
  if (fileInput.value) fileInput.value.value = '';
  try {
    const { data } = await dealAttachmentsAPI(editingId.value).upload(fd);
    attachments.value = data.payload || attachments.value;
  } finally {
    uploadingFiles.value = [];
  }
};
const removeAttachment = async att => {
  await dealAttachmentsAPI(editingId.value).delete(att.id);
  attachments.value = attachments.value.filter(a => a.id !== att.id);
};
// The API stores cents; the row shows the value the way the user types money.
const decorateItems = rows =>
  rows.map(row => ({
    ...row,
    // the API returns a decimal ("2.0"); the field should read like a count
    quantity: Number(row.quantity),
    unitAmountInput: formatAmountInput(row.unit_amount_cents, locale.value),
  }));

const loadItems = async dealId => {
  try {
    items.value = decorateItems(
      (await dealItemsAPI(dealId).list()).data.payload || []
    );
  } catch (e) {
    items.value = [];
  }
};
const loadItemTemplates = async () => {
  try {
    itemTemplates.value = (await itemTemplatesAPI.list()).data.payload || [];
  } catch (e) {
    itemTemplates.value = [];
  }
};
const reloadItems = async payload => {
  items.value = payload.payload ? decorateItems(payload.payload) : items.value;
  if (payload.total_cents != null)
    form.amount = formatAmountInput(payload.total_cents, locale.value);
};
const addItem = async (preset = null) => {
  if (!editingId.value) return;
  const body = preset
    ? {
        name: preset.name,
        quantity: 1,
        unit_amount_cents: preset.unit_amount_cents,
      }
    : {
        name: t('WASAPI_CRM.ITEMS.NEW_ITEM'),
        quantity: 1,
        unit_amount_cents: 0,
      };
  const { data } = await dealItemsAPI(editingId.value).create(body);
  await reloadItems(data);
};
const addFromTemplate = async () => {
  const tpl = itemTemplates.value.find(
    x => x.id === Number(pickedTemplateId.value)
  );
  // Reset on the next tick: clearing in the same tick as the pick leaves the ComboBox
  // stuck on the template label because it never sees the value change.
  await nextTick();
  pickedTemplateId.value = '';
  if (tpl) await addItem(tpl);
};
const updateItem = async item => {
  const { data } = await dealItemsAPI(editingId.value).update(item.id, {
    name: item.name,
    quantity: item.quantity,
    unit_amount_cents: item.unit_amount_cents,
  });
  await reloadItems(data);
};
const updateItemAmount = item => {
  item.unit_amount_cents = parseAmount(item.unitAmountInput) ?? 0;
  return updateItem(item);
};
const removeItem = async item => {
  const { data } = await dealItemsAPI(editingId.value).delete(item.id);
  await reloadItems(data);
};
const saveAsTemplate = async item => {
  await itemTemplatesAPI.create({
    name: item.name,
    unit_amount_cents: item.unit_amount_cents,
  });
  await loadItemTemplates();
};

// Debounced, and answers that arrive out of order are dropped: typing "Nik" fires "N", "Ni",
// "Nik" and the slowest reply (usually the broadest) must not overwrite the newest one.
let contactSearchTimer = null;
let contactSearchSeq = 0;
const searchContacts = query => {
  clearTimeout(contactSearchTimer);
  if (!query) return;
  contactSearchTimer = setTimeout(async () => {
    contactSearchSeq += 1;
    const seq = contactSearchSeq;
    try {
      const { data } = await ContactAPI.search(query, 1, 'name');
      if (seq !== contactSearchSeq) return;
      contactOptions.value = (data.payload || []).map(c => ({
        value: c.id,
        label: [c.name, c.phone_number].filter(Boolean).join(' · '),
      }));
    } catch {
      if (seq === contactSearchSeq) contactOptions.value = [];
    }
  }, 250);
};

const handleConfirm = async () => {
  if (isInvalid.value) return;
  const payload = {
    name: form.name.trim(),
    amountCents: parseAmount(form.amount) ?? 0,
    stageId: form.stageId,
    contactId: form.contactId || null,
    conversationId: form.conversationId || null,
    assigneeId: form.assigneeId || null,
    expectedCloseDate: form.expectedCloseDate || null,
    description: form.description.trim() || null,
    customAttributes: form.customAttributes,
    closeReasonId: closeReasons.value.length
      ? form.closeReasonId || null
      : null,
  };
  try {
    const deal = isEditing.value
      ? await store.updateDeal(editingId.value, payload)
      : await store.createDeal(payload);
    useAlert(
      t(isEditing.value ? 'WASAPI_CRM.DEAL.UPDATED' : 'WASAPI_CRM.DEAL.CREATED')
    );
    emit('saved', deal);
    dialogRef.value?.close();
  } catch {
    useAlert(t('WASAPI_CRM.DEAL.SAVE_ERROR'));
  }
};

const step = ref(1);
const stepOptions = computed(() => [
  { value: 1, label: t('WASAPI_CRM.DEAL.STEP_DATA') },
  { value: 2, label: t('WASAPI_CRM.DEAL.STEP_EXTRAS') },
]);
const confirmRef = ref(null);
const handleDelete = () =>
  confirmRef.value?.open({
    title: t('WASAPI_CRM.DEAL.DELETE_CONFIRM', { name: form.name }),
    confirmLabel: t('WASAPI_CRM.DEAL.DELETE'),
    onConfirm: async () => {
      try {
        await store.deleteDeal(editingId.value);
        useAlert(t('WASAPI_CRM.DEAL.DELETED'));
        emit('deleted', editingId.value);
        dialogRef.value?.close();
      } catch {
        useAlert(t('WASAPI_CRM.DEAL.DELETE_ERROR'));
      }
    },
  });

const open = ({ deal = null, defaults = {} } = {}) => {
  step.value = 1;
  Object.assign(form, blankForm());
  editingId.value = deal?.id || null;
  if (deal) {
    form.name = deal.name;
    form.amount = deal.amountCents
      ? formatAmountInput(deal.amountCents, locale.value)
      : '';
    form.stageId = deal.stageId;
    form.contactId = deal.contact?.id || '';
    form.conversationId = deal.conversationId;
    form.assigneeId = deal.assigneeId || '';
    form.expectedCloseDate = deal.expectedCloseDate || '';
    form.closeReasonId = deal.closeReasonId || '';
    form.description = deal.description || '';
    form.customAttributes = { ...(deal.customAttributes || {}) };
    contactLabel.value = deal.contact?.name || '';
    loadTasks(deal.id);
    loadItems(deal.id);
    loadAttachments(deal.id);
  } else {
    tasks.value = [];
    items.value = [];
    attachments.value = [];
    form.stageId = defaults.stageId || props.stages[0]?.id || '';
    form.contactId = defaults.contactId || '';
    form.conversationId = defaults.conversationId || null;
    form.name = defaults.name || '';
    contactLabel.value = defaults.contactName || '';
  }
  if (form.contactId && contactLabel.value) {
    contactOptions.value = [
      { value: form.contactId, label: contactLabel.value },
    ];
  }
  loadItemTemplates();
  dialogRef.value?.open();
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="
      t(isEditing ? 'WASAPI_CRM.DEAL.EDIT_TITLE' : 'WASAPI_CRM.DEAL.NEW_TITLE')
    "
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <DialogSteps v-if="isEditing" v-model="step" :steps="stepOptions" />
    <div v-show="step === 1" class="flex flex-col gap-4">
      <Input
        v-model="form.name"
        :label="t('WASAPI_CRM.DEAL.FIELDS.NAME')"
        :placeholder="t('WASAPI_CRM.DEAL.FIELDS.NAME_PLACEHOLDER')"
        :disabled="isSaving"
        autofocus
      />
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div v-if="hasItems" class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WASAPI_CRM.DEAL.FIELDS.AMOUNT') }}
          </label>
          <div
            class="px-3 py-2 text-sm rounded-lg bg-n-alpha-1 text-n-slate-12 tabular-nums"
          >
            {{ money(itemsTotalCents) }}
          </div>
          <span class="text-xs text-n-slate-10">{{
            t('WASAPI_CRM.ITEMS.AMOUNT_FROM_ITEMS')
          }}</span>
        </div>
        <Input
          v-else
          v-model="form.amount"
          type="text"
          inputmode="decimal"
          :label="t('WASAPI_CRM.DEAL.FIELDS.AMOUNT')"
          placeholder="1.500,00"
          :disabled="isSaving"
          :message="
            parseAmount(form.amount) === null
              ? t('WASAPI_CRM.DEAL.FIELDS.AMOUNT_INVALID')
              : ''
          "
          :message-type="parseAmount(form.amount) === null ? 'error' : 'info'"
        />
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WASAPI_CRM.DEAL.FIELDS.STAGE') }}
          </label>
          <ComboBox
            v-model="form.stageId"
            :options="stagePickerOptions"
            class="w-full"
          />
        </div>
      </div>
      <div v-if="closeReasons.length" class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{
            targetStage?.category === 'won'
              ? t('WASAPI_CRM.CLOSE_REASON.WON_TITLE')
              : t('WASAPI_CRM.CLOSE_REASON.LOST_TITLE')
          }}
        </label>
        <ComboBox
          v-model="form.closeReasonId"
          :options="closeReasonOptions"
          :placeholder="t('WASAPI_CRM.CLOSE_REASON.PLACEHOLDER')"
          class="w-full"
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WASAPI_CRM.DEAL.FIELDS.CONTACT') }}
        </label>
        <ComboBox
          v-model="form.contactId"
          :options="contactOptions"
          :display-label="contactLabel"
          :placeholder="t('WASAPI_CRM.DEAL.FIELDS.CONTACT_PLACEHOLDER')"
          :search-placeholder="t('WASAPI_CRM.DEAL.FIELDS.CONTACT_SEARCH')"
          :empty-state="t('WASAPI_CRM.DEAL.FIELDS.CONTACT_EMPTY')"
          :disabled="isSaving || lockContact"
          use-api-results
          @search="searchContacts"
        />
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-n-slate-12">
            {{ t('WASAPI_CRM.DEAL.FIELDS.ASSIGNEE') }}
          </label>
          <ComboBox
            v-model="form.assigneeId"
            :options="agentOptions"
            :placeholder="t('WASAPI_CRM.DEAL.FIELDS.ASSIGNEE_PLACEHOLDER')"
            class="w-full"
          />
        </div>
        <Input
          v-model="form.expectedCloseDate"
          type="date"
          :label="t('WASAPI_CRM.DEAL.FIELDS.EXPECTED_CLOSE_DATE')"
          :disabled="isSaving"
        />
      </div>
      <div
        v-if="customFields.length"
        class="grid grid-cols-1 gap-4 sm:grid-cols-2"
      >
        <div
          v-for="field in customFields"
          :key="field.key"
          class="flex flex-col gap-1"
        >
          <label class="text-sm font-medium text-n-slate-12">{{
            field.label
          }}</label>
          <ComboBox
            v-if="field.type === 'select'"
            v-model="form.customAttributes[field.key]"
            :options="(field.options || []).map(o => ({ value: o, label: o }))"
            :placeholder="t('WASAPI_CRM.FIELDS.SELECT_PLACEHOLDER')"
            class="w-full"
          />
          <label
            v-else-if="field.type === 'checkbox'"
            class="flex items-center gap-2 h-10 text-sm text-n-slate-11"
          >
            <input v-model="form.customAttributes[field.key]" type="checkbox" />
            {{ t('WASAPI_CRM.FIELDS.YES') }}
          </label>
          <Input
            v-else
            v-model="form.customAttributes[field.key]"
            :type="
              field.type === 'number'
                ? 'number'
                : field.type === 'date'
                  ? 'date'
                  : 'text'
            "
          />
        </div>
      </div>
      <TextArea
        v-model="form.description"
        :placeholder="t('WASAPI_CRM.DEAL.FIELDS.DESCRIPTION')"
        :disabled="isSaving"
        :max-length="1000"
        class="w-full"
        auto-height
      />
    </div>

    <div v-show="step === 2" class="flex flex-col gap-4">
      <div v-if="isEditing" class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('WASAPI_CRM.ITEMS.TITLE') }}
          </span>
          <span
            v-if="hasItems"
            class="text-sm font-medium text-n-slate-12 tabular-nums"
          >
            {{ money(itemsTotalCents) }}
          </span>
        </div>
        <div
          v-for="item in items"
          :key="item.id"
          class="grid grid-cols-[1fr_3.5rem_6rem_auto] items-center gap-2"
        >
          <Input
            v-model="item.name"
            size="sm"
            :placeholder="t('WASAPI_CRM.ITEMS.NAME')"
            @blur="updateItem(item)"
          />
          <Input
            v-model="item.quantity"
            type="number"
            size="sm"
            :placeholder="t('WASAPI_CRM.ITEMS.QUANTITY')"
            @blur="updateItem(item)"
          />
          <Input
            v-model="item.unitAmountInput"
            size="sm"
            :placeholder="t('WASAPI_CRM.ITEMS.UNIT_PRICE')"
            @blur="updateItemAmount(item)"
          />
          <div class="flex items-center gap-0.5">
            <Button
              v-tooltip="t('WASAPI_CRM.ITEMS.SAVE_TO_CATALOG')"
              icon="i-lucide-bookmark-plus"
              variant="ghost"
              color="slate"
              size="xs"
              @click="saveAsTemplate(item)"
            />
            <Button
              icon="i-lucide-trash-2"
              variant="ghost"
              color="ruby"
              size="xs"
              @click="removeItem(item)"
            />
          </div>
        </div>
        <div class="flex items-center gap-2">
          <Button
            class="shrink-0"
            :label="t('WASAPI_CRM.ITEMS.ADD')"
            icon="i-lucide-plus"
            variant="faded"
            color="slate"
            size="sm"
            @click="addItem()"
          />
          <ComboBox
            v-model="pickedTemplateId"
            :options="templateOptions"
            class="max-w-64"
            @update:model-value="addFromTemplate"
          />
        </div>
      </div>

      <div
        v-if="isEditing"
        class="flex flex-col gap-2 pt-2 border-t border-n-weak"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('WASAPI_CRM.ATTACHMENTS.TITLE') }}
        </span>
        <a
          v-for="att in attachments"
          :key="att.id"
          :href="att.url"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-2 px-2 py-1 text-sm rounded bg-n-alpha-1 hover:bg-n-alpha-2"
        >
          <Icon icon="i-lucide-paperclip" class="size-3.5 text-n-slate-10" />
          <span class="flex-1 truncate text-n-slate-12">{{
            att.filename
          }}</span>
          <Button
            icon="i-lucide-trash-2"
            variant="ghost"
            color="ruby"
            size="xs"
            @click.prevent="removeAttachment(att)"
          />
        </a>
        <div
          v-for="name in uploadingFiles"
          :key="`uploading-${name}`"
          class="flex items-center gap-2 px-2 py-1 text-sm rounded bg-n-alpha-1 opacity-70"
        >
          <Spinner :size="14" class="text-n-slate-10" />
          <span class="flex-1 truncate text-n-slate-12">{{ name }}</span>
        </div>
        <input
          ref="fileInput"
          type="file"
          multiple
          class="hidden"
          @change="onUpload"
        />
        <Button
          :label="t('WASAPI_CRM.ATTACHMENTS.ADD')"
          icon="i-lucide-upload"
          variant="faded"
          color="slate"
          size="sm"
          class="self-start"
          :is-loading="uploadingFiles.length > 0"
          :disabled="uploadingFiles.length > 0"
          @click="fileInput.click()"
        />
      </div>

      <div
        v-if="isEditing"
        class="flex flex-col gap-2 pt-2 border-t border-n-weak"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('WASAPI_CRM.TASKS.TITLE') }}
        </span>
        <div
          v-for="task in tasks"
          :key="task.id"
          class="flex items-center gap-2 text-sm"
        >
          <input
            type="checkbox"
            class="!mb-0"
            :checked="task.done"
            @change="toggleTask(task)"
          />
          <span
            class="flex-1 truncate"
            :class="
              task.done ? 'line-through text-n-slate-10' : 'text-n-slate-12'
            "
          >
            {{ task.title }}
          </span>
          <Button
            icon="i-lucide-trash-2"
            variant="ghost"
            color="ruby"
            size="xs"
            @click="removeTask(task)"
          />
        </div>
        <div class="flex items-center gap-2">
          <Input
            v-model="newTask"
            class="flex-1"
            size="sm"
            :placeholder="t('WASAPI_CRM.TASKS.NEW')"
            @keydown.enter.prevent.stop="addTask"
          />
          <Button
            :label="t('WASAPI_CRM.TASKS.ADD')"
            variant="faded"
            color="slate"
            size="sm"
            @click="addTask"
          />
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          v-if="isEditing"
          :label="t('WASAPI_CRM.DEAL.DELETE')"
          icon="i-lucide-trash-2"
          variant="ghost"
          color="ruby"
          :disabled="isSaving"
          @click="handleDelete"
        />
        <span v-else />
        <div class="flex items-center gap-3">
          <Button
            v-if="isEditing && step === 2"
            :label="t('WASAPI_COMMON.BACK')"
            icon="i-lucide-arrow-left"
            variant="ghost"
            color="slate"
            @click="step = 1"
          />
          <Button
            :label="t('DIALOG.BUTTONS.CANCEL')"
            variant="faded"
            color="slate"
            @click="dialogRef.close()"
          />
          <Button
            v-if="isEditing && step === 1"
            :label="t('WASAPI_COMMON.NEXT')"
            variant="faded"
            color="slate"
            @click="step = 2"
          />
          <Button
            :label="
              t(isEditing ? 'WASAPI_CRM.DEAL.SAVE' : 'WASAPI_CRM.DEAL.CREATE')
            "
            color="blue"
            :disabled="isInvalid || isSaving"
            :is-loading="isSaving"
            @click="handleConfirm"
          />
        </div>
      </div>
    </template>
  </Dialog>
  <ConfirmDialog ref="confirmRef" />
</template>
